import request from '@/utils/request'
import locale from './../lang/i18n'
import { adminPath, deleteMedia, updateSettings, uploadMedia } from './adminV1'

function publicLocale () {
  return locale.locale === 'en-US' ? 'en' : 'zh-cn'
}

function seriesPayload (data) {
  return {
    name: data.name,
    indexDescription: data.indexDescription || data.indexDesc,
    content: data.content || data.productDesc,
    publishDate: data.publishDate,
    indexCoverUrl: data.indexCoverUrl || data.coverIndexURL,
    detailCoverUrl: data.detailCoverUrl || data.coverProductURL,
    slug: data.slug,
    translationGroup: data.translationGroup,
    seoTitle: data.seoTitle,
    metaDescription: data.metaDescription,
    coverAlt: data.coverAlt || data.name,
    publishStatus: data.publishStatus || 'PUBLISHED'
  }
}

function productPayload (data) {
  return {
    name: data.name,
    seriesId: data.seriesId,
    content: data.content,
    publishDate: data.publishDate,
    coverUrl: data.coverUrl || data.coverURL,
    videoUrl: data.videoUrl || data.videoURL,
    slug: data.slug,
    translationGroup: data.translationGroup,
    seoTitle: data.seoTitle,
    metaDescription: data.metaDescription,
    coverAlt: data.coverAlt || data.name,
    publishStatus: data.publishStatus || 'PUBLISHED'
  }
}

export function uploadIndexCover (file) {
  return uploadMedia(file, 'image').then(data => ({ code: 0, data: data.url }))
}

export function delIndexCover (seriesId, url) {
  return deleteMedia(url).then(() => ({ code: 0 }))
}

export function uploadProductCover (file) {
  return uploadMedia(file, 'image').then(data => ({ code: 0, data: data.url }))
}

export function delProductCover (seriesId, url) {
  return deleteMedia(url).then(() => ({ code: 0 }))
}

export function addSeries (data) {
  return request({ url: adminPath('/product-series'), method: 'post', data: seriesPayload(data) })
    .then(result => ({ code: 0, data: result }))
}

export function updateSeries (data) {
  return request({ url: adminPath(`/product-series/${data.id}`), method: 'put', data: seriesPayload(data) })
    .then(result => ({ code: 0, data: result }))
}

export function getProductSeriesList () {
  return request({ url: `/api/public/v1/${publicLocale()}/product-series`, method: 'get' })
    .then(list => ({
      code: 0,
      data: (list || []).map(item => ({
        ...item,
        id: item.slug,
        indexDesc: item.description,
        productDesc: item.description,
        coverIndexURL: item.coverUrl,
        coverProductURL: item.productCoverUrl || item.coverUrl
      }))
    }))
}

export function getAdminSeriesList () {
  return request({ url: adminPath('/product-series'), method: 'get' })
    .then(data => ({ code: 0, data }))
}

export function deleteSeries (id, cascade) {
  return request({
    url: adminPath(`/product-series/${id}`),
    method: 'delete',
    params: { cascade: Boolean(cascade) }
  }).then(() => ({ code: 0 }))
}

export function settingTopBackground (file) {
  return uploadMedia(file, 'image')
    .then(media => updateSettings({ productTopImage: media.url }))
    .then(data => ({ code: 0, data }))
}

export function getTopBackground () {
  return request({ url: adminPath('/settings'), method: 'get' })
    .then(data => ({ code: 0, data: { value: data.productTopImage || '' } }))
}

export function getWebTopBackground () {
  return request({ url: `/api/public/v1/${publicLocale()}/media/PRODUCT_TOP_IMG`, method: 'get' })
    .then(data => ({ code: 0, data }))
}

export function getProductVideoList () {
  return request({ url: `/api/public/v1/${publicLocale()}/products`, method: 'get' })
    .then(list => ({
      code: 0,
      data: (list || []).filter(item => item.videoUrl).map(item => ({
        ...item,
        id: item.slug,
        coverURL: item.coverUrl,
        videoURL: item.videoUrl
      }))
    }))
}

export function getAdminProductList (data) {
  const query = data || {}
  return request({
    url: adminPath('/products/page'),
    method: 'get',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize,
      searchName: String(query.searchName || query.searchTitle || '').trim() || undefined,
      seriesId: query.seriesId || undefined
    }
  }).then(page => ({
    code: 0,
    data: {
      rows: page.rows.map(item => ({
        ...item,
        pid: item.id,
        productName: item.name,
        productPublishDate: item.publishDate
      })),
      total: page.total
    }
  }))
}

export function deleteProduct (id) {
  return request({ url: adminPath(`/products/${id}`), method: 'delete' })
    .then(() => ({ code: 0 }))
}

export function uploadCover (file) {
  return uploadMedia(file, 'image').then(data => ({ code: 0, data: data.url }))
}

export function delCover (productId, url) {
  return deleteMedia(url).then(() => ({ code: 0 }))
}

export function uploadProductMedia (file) {
  const uploaded = file && file.get ? file.get('file') : null
  const kind = uploaded && uploaded.type && uploaded.type.startsWith('video/') ? 'video' : 'image'
  return uploadMedia(file, kind).then(data => ({ code: 0, data }))
}

export function deleteProductMedia (url) {
  return deleteMedia(url).then(() => ({ code: 0 }))
}

export function addProduct (data) {
  return request({ url: adminPath('/products'), method: 'post', data: productPayload(data) })
    .then(result => ({ code: 0, data: result }))
}

export function getProductContent (id) {
  return request({ url: adminPath(`/products/${id}`), method: 'get' })
    .then(data => ({ code: 0, data }))
}

export function getWebProductContent (slug) {
  return request({
    url: `/api/public/v1/${publicLocale()}/products/${encodeURIComponent(slug)}`,
    method: 'get'
  }).then(data => ({
    code: 0,
    data: { name: data.name, content: data.content, coverURL: data.coverUrl, videoURL: data.videoUrl }
  }))
}

export function updateProductContent (data) {
  return request({ url: adminPath(`/products/${data.id}`), method: 'put', data: productPayload(data) })
    .then(result => ({ code: 0, data: result }))
}

export function getSeriesProductList (id) {
  return request({ url: adminPath('/products'), method: 'get', params: { seriesId: id } })
    .then(data => ({ code: 0, data }))
}

export function getWebSeriesProductList (slug) {
  return request({
    url: `/api/public/v1/${publicLocale()}/product-series/${encodeURIComponent(slug)}`,
    method: 'get'
  }).then(data => ({
    code: 0,
    data: (data.products || []).map(item => ({
      id: item.slug,
      name: item.name,
      content: item.content,
      coverURL: item.coverUrl,
      videoURL: item.videoUrl
    }))
  }))
}
